import type { HazardEvent } from "@eldrun/protocol";
import type { Object3D } from "three";
import type { AtmosphereUniforms } from "../render/atmosphere.ts";
import { DecalBatch } from "../render/decal-batch.ts";
import { createHazardMaterial, HAZARD_KIND } from "../render/shaders/hazard.ts";
import { stripGeometry, telegraphGeometry, type HeightAt } from "./telegraphs.ts";

interface Decal {
  id: string;
  piece: ReturnType<DecalBatch["add"]>;
  kind: number;
  born: number;
  ms: number;
}

/** Forma no chão do perigo: faixa (rastro de fogo) ou disco (poça, mato, brasas, fogo parado). */
export function hazardGeometry(e: HazardEvent, height: HeightAt) {
  if (e.kind === "fogo" && (e.x !== e.x2 || e.z !== e.z2)) {
    const dx = e.x2 - e.x;
    const dz = e.z2 - e.z;
    return stripGeometry(e.x, e.z, Math.atan2(-dx, -dz), Math.max(0.5, Math.hypot(dx, dz)), e.radius * 2, height);
  }
  return telegraphGeometry({ shape: "circulo", x: e.x, z: e.z, yaw: 0, range: e.radius, angle: 360 }, height);
}

/**
 * Áreas que machucam por um tempo (doc 06, Bestiário): rastro de fogo da Mula e poça do caldeirão
 * da Cuca. Só visual; o servidor aplica o dano e a lentidão.
 */
export class HazardDecals {
  private readonly decals: Decal[] = [];
  /** Em lote (revisão da Fase 13): um draw para todos os perigos, não um por perigo. */
  private readonly batch: DecalBatch;

  constructor(root: Object3D, atmosphere: AtmosphereUniforms) {
    this.batch = new DecalBatch(root, () => createHazardMaterial(atmosphere), 4);
  }

  spawn(e: HazardEvent, now: number, height: HeightAt): void {
    const piece = this.batch.add(hazardGeometry(e, height));
    const kind = HAZARD_KIND[e.kind];
    this.batch.set(piece, kind, 0, 0, 0);
    this.decals.push({ id: e.id, piece, kind, born: now, ms: e.ms });
    this.batch.flush();
  }

  update(now: number): void {
    for (let i = this.decals.length - 1; i >= 0; i--) {
      const d = this.decals[i] as Decal;
      const life = (now - d.born) / d.ms;
      if (life >= 1) {
        this.drop(i);
        continue;
      }
      this.batch.set(d.piece, d.kind, life, 0, 0);
    }
    this.batch.flush();
  }

  /** Draw calls dos perigos agora. */
  get draws(): number {
    return this.batch.draws;
  }

  get count(): number {
    return this.decals.length;
  }

  /** Apaga antes da hora (o Boitatá limpa o convés na troca de fase). */
  clear(ids: readonly string[]): void {
    const set = new Set(ids);
    for (let i = this.decals.length - 1; i >= 0; i--) if (set.has((this.decals[i] as Decal).id)) this.drop(i);
    this.batch.flush();
  }

  dispose(): void {
    for (let i = this.decals.length - 1; i >= 0; i--) this.drop(i);
    this.batch.dispose();
  }

  private drop(i: number): void {
    const d = this.decals[i] as Decal;
    this.batch.remove(d.piece);
    this.decals.splice(i, 1);
  }
}
